import React, {ReactElement, useEffect, useRef} from "react";
import classNames, {Argument} from "classnames";
import styles from "./pdf-viewer.module.scss";

export type PdfViewerStatus = "loading" | "ready" | "error";

export interface PdfViewerProps {
	/**
	 * Where to fetch the pdf from. A missing url is reported as an error.
	 */
	url: string | null | undefined;
	/**
	 * Bump to reload the document after a failure.
	 */
	retryKey?: number;
	/**
	 * Reports loading progress. "ready" fires once every page has been drawn.
	 */
	onStatusChange: (status: PdfViewerStatus) => void;
	/**
	 * Fires after each page is drawn, so a parent can re-measure the scroll height.
	 */
	onPageRendered?: () => void;
	className?: Argument;
}

const FALLBACK_WIDTH = 600;

/**
 * Renders every page of a pdf as a stacked canvas, sized to the container width.
 * pdf.js is loaded on demand so surveys without a consent question never pay for it.
 */
const PdfViewer = ({url, retryKey = 0, onStatusChange, onPageRendered, className}: PdfViewerProps): ReactElement => {
	const containerRef = useRef<HTMLDivElement>(null);
	// The load effect only re-runs for a new document, so it reads the callbacks through refs.
	const onStatusChangeRef = useRef(onStatusChange);
	const onPageRenderedRef = useRef(onPageRendered);
	onStatusChangeRef.current = onStatusChange;
	onPageRenderedRef.current = onPageRendered;

	useEffect(() => {
		const container = containerRef.current;
		if (!container) return undefined;
		container.replaceChildren();

		if (!url) {
			onStatusChangeRef.current("error");
			return undefined;
		}

		let cancelled = false;
		let loadingTask: {destroy: () => Promise<void>} | undefined;
		let renderTask: {cancel: () => void} | undefined;
		onStatusChangeRef.current("loading");

		const load = async (): Promise<void> => {
			const pdfjs = await import("pdfjs-dist");
			const worker = await import("pdfjs-dist/build/pdf.worker.min.mjs?url");
			pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
			if (cancelled) return;

			const task = pdfjs.getDocument({url});
			loadingTask = task;
			const doc = await task.promise;
			if (cancelled) return;

			const width = container.clientWidth || FALLBACK_WIDTH;
			const dpr = window.devicePixelRatio || 1;

			for (let pageNumber = 1; pageNumber <= doc.numPages; pageNumber++) {
				// eslint-disable-next-line no-await-in-loop
				const page = await doc.getPage(pageNumber);
				if (cancelled) return;
				const scale = width / page.getViewport({scale: 1}).width;
				const viewport = page.getViewport({scale: scale * dpr});
				const canvas = document.createElement("canvas");
				canvas.className = styles.page;
				canvas.width = Math.floor(viewport.width);
				canvas.height = Math.floor(viewport.height);
				canvas.style.width = `${width}px`;
				canvas.style.height = `${Math.floor(viewport.height / dpr)}px`;
				canvas.setAttribute("aria-label", `Page ${pageNumber} of ${doc.numPages}`);
				container.appendChild(canvas);
				const currentRender = page.render({canvas, viewport});
				renderTask = currentRender;
				// eslint-disable-next-line no-await-in-loop
				await currentRender.promise;
				if (cancelled) return;
				onPageRenderedRef.current?.();
			}
			onStatusChangeRef.current("ready");
		};

		load().catch(() => {
			if (!cancelled) onStatusChangeRef.current("error");
		});

		return () => {
			cancelled = true;
			renderTask?.cancel();
			loadingTask?.destroy().catch(() => undefined);
			container.replaceChildren();
		};
	}, [url, retryKey]);

	return <div ref={containerRef} className={classNames(styles.pages, className)} role="document" />;
};

export {PdfViewer};
